<template>
  <div class="content">
    <h1 class="has-text-centered">FTP Protokoll</h1>
    <div class="box">
      <p>Eine kompakte Zusammenfassung über das FTP Protokoll.</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-ftp-protocol',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: 'FTP Protokoll | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Eine kompakte Zusammenfassung über das FTP Protokoll.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was ist FTP?',
          answer:
            '<ul>' +
            '  <li>FTP = <strong>F</strong>ile <strong>T</strong>ransfer <strong>P</strong>rotocol</li>' +
            '  <li>Gehört zur Internetprotokollfamilie</li>' +
            '  <li>Befindet sich auf Schicht 7 (Anwendungsschicht) des OSI-Modells</li>' +
            '  <li>Benutzt zwei Ports</li>' +
            '  <ul>' +
            '    <li><strong>Port 20:</strong> Data Port für die Datenverbindung</li>' +
            '    <li><strong>Port 21:</strong> Control Port für die Steuerung der Verbindung</li>' +
            '  </ul>' +
            '  <li>Es gibt zwei Arten von FTP</li>' +
            '  <ul>' +
            '    <li>Aktives FTP</li>' +
            '    <li>Passives FTP</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist ein RFC?',
          answer:
            '<ul>' +
            '  <li>RFC = <strong>R</strong>equest <strong>f</strong>or <strong>C</strong>omments</li>' +
            '  <li>Dokumente die von der IETF (Internet Engineering Task Force) herausgegeben werden</li>' +
            '  <li>Behandeln Protokolle, Methode, Programme und Konzepte für die Zusammenarbeit unterschiedlicher Systeme im Internet</li>' +
            '</ul>'
        },
        {
          question: 'Wofür wird FTP benutzt?',
          answer:
            '<ul>' +
            '  <li>' +
            '    <strong>Datenübertragung:</strong> Daten auf einen Server hoch- oder runterladen' +
            '  </li>' +
            '  <li>' +
            '    <strong>Dateiverwaltung:</strong> Verzeichnisse anlegen und auslesen sowie Verzeichnisse und Dateien umbenennen oder löschen' +
            '  </li>' +
            '</ul>'
        },
        {
          question: 'Was ist ein aktives FTP?',
          answer:
            '<ul>' +
            '  <li>Der Aufbau einer aktiven FTP Sitzung umfasst folgende Schritte:</li>' +
            '  <ol>' +
            '    <li>Client (ein Port > 1023) sendet PORT Kommando an den FTP Server (Port 21)</li>' +
            '    <li>Server antwortet mit ACK</li>' +
            '    <li>Server (Port 20) baut Verbindung mit dem Client auf (Client-Port wurde in PORT Kommando übermittelt)</li>' +
            '    <li>Der Client erwidert mit einem ACK an den Server - die Verbindung wurde aufgebaut</li>' +
            '  </ol>' +
            '</ul>'
        },
        {
          question: 'Was ist ein passives FTP?',
          answer:
            '<ul>' +
            '  <li>Ein Modus der mögliche Probleme mit clientseitigen Firewalls umgeht</li>' +
            '  <li>Der Client baut die Verbindung zum Server auf, was die meisten Firewall zulassen (ausgehende Verbindung)</li>' +
            '  <li>Folgende Schritte sind notwendig:</li>' +
            '  <ol>' +
            '    <li>Client (ein Port > 1023) sendet PASV Kommando an den FTP Server (Port 21)</li>' +
            '    <li>Server antwortet mit PORT Kommando (beinhaltet Information über einen verfügbaren Port > 1023 am FTP Server)</li>' +
            '    <li>Client (Port > 1023) baut Verbindung mit dem Server auf (Port in PORT Kommando übermittelt)</li>' +
            '    <li>Server antwortet mit ACK und die Verbindung ist bereit.</li>' +
            '  </ol>' +
            '</ul>'
        },
        {
          question: 'Was ist FTPS?',
          answer:
            '<ul>' +
            '  <li>FTPS = <strong>F</strong>ile <strong>T</strong>ransfer <strong>P</strong>rotocol <strong>S</strong>ecure</li>' +
            '  <li>Verschlüsselung und Authentifizierung mithilfe von TLS (<strong>T</strong>ransport <strong>L</strong>ayer <strong>S</strong>ecurity)</li>' +
            '</ul>' +
            '<h3>Ablauf von FTPS</h3>' +
            '<ol>' +
            '  <li>Der Client baut einen herkömmlichen unverschlüsselten Kontrollkanal zum Server auf</li>' +
            '  <li>' +
            '    Der Client sendet Aufforderung zur TLS Authentifizierung<br />' +
            '    Bleibt diese Aufforderung aus, kann der Server alle anderen Aufforderungen ablehnen' +
            '  </li>' +
            '  <li>' +
            '    Der Server sendet als Ankündigung des Wechsels zu TLS die Antwort 234<br />' +
            '    Bleibt diese Antwort aus, kann der Client unverschlüsselt fortfahren' +
            '  </li>' +
            '  <li>' +
            '    Ist auf dem Kontrollkanal zu TLS gewechselt, kann der Client zusätzlich TLS der Nutzdatenkanäle anfordern<br />' +
            '    Alternativ kann der Client jederzeit die Rückkehr zur unverschlüsselten Verbindung fordern, was der Server aber verweigern kann.' +
            '  </li>' +
            '</ol>'
        },
        {
          question: 'Was ist SFTP?',
          answer:
            '<ul>' +
            '  <li>SFTP = <strong>S</strong>SH <strong>F</strong>ile <strong>T</strong>ransfer <strong>P</strong>rotocol</li>' +
            '  <li>Verschlüsselung und Funktion wird über SSH (<strong>S</strong>ecure <strong>Sh</strong>ell) ermöglich</li>' +
            '  <li>Da die Verbindung über SSH läuft wird Port 22 benutzt</li>' +
            '</ul>'
        },
        {
          question: 'Was ist SSH?',
          answer:
            '<ul>' +
            '  <li>SSH = <strong>S</strong>ecure <strong>Sh</strong>ell</li>' +
            '  <li>Ein Netzwerkprotokoll</li>' +
            '  <li>Ermöglicht sichere Netzwerkverbindung mit einem entfernten Gerät</li>' +
            '  <li>Wird häufig eingesetzt um Systeme über Terminals fernzuwarten</li>' +
            '</ul>'
        }
      ]
    }
  }
}
</script>
